import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import ReactFlow, {
  addEdge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Connection,
  Edge,
  Node,
  ReactFlowProvider,
  useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import BaseNode from './BaseNode.tsx';
import {
  Box,
  Button,
  Breadcrumbs,
  Typography,
  Link,
  Fab,
  Zoom,
  Paper,
  TextField,
  IconButton,
  Chip,
} from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { botFlowDataAtom, flowObjAtom } from '../../../Services/_states';
import CustomEdge from './CustomEdge.tsx';
import { FlowBuilderActions } from '../../../Services/_actions/index.js';
import { useNavigate, useParams } from 'react-router';
import { useSnackBar } from '../Common/SnackBar/index.tsx';
import LoadingWrapper from '../../Shared/LoadingWrapper/index.jsx';
import { Flow, FormData } from '../../../interfaces/Flowbuilder.interface.ts';
import DownloadButton from './DownloadButton.tsx';
import { handleDownloadExcelFile } from './DownloadExcelButton.tsx';
import ShowTableModal from './ShowTableModal.tsx';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import {
  TextFields as TextFieldsIcon,
  Image as ImageIcon,
  Videocam as VideocamIcon,
  Audiotrack as AudiotrackIcon,
  InsertDriveFile as InsertDriveFileIcon,
  TouchApp as TouchAppIcon,
  List as ListIcon,
  Contacts as ContactsIcon,
  ViewModule as ViewModuleIcon,
  ShoppingBag as ShoppingBagIcon,
  WhatsApp as WhatsAppIcon,
  AccountTree as AccountTreeIcon,
  LocationOn as LocationOnIcon,
  Description as DescriptionIcon,
  Link as LinkIcon,
  MenuBook as MenuBookIcon,
  Rule as ConditionIcon,
} from '@mui/icons-material';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import { insideFlowNodes } from './insideFlowNodes.ts';

const nodeIcons = {
  text: TextFieldsIcon,
  image: ImageIcon,
  video: VideocamIcon,
  audio: AudiotrackIcon,
  document: InsertDriveFileIcon,
  button: TouchAppIcon,
  list: ListIcon,
  intervention: ContactsIcon,
  catalog: ViewModuleIcon,
  product: ShoppingBagIcon,
  whatsAppFlow: WhatsAppIcon,
  existingFlow: AccountTreeIcon,
  address: LocationOnIcon,
  openAi: DescriptionIcon,
  ctaUrl: LinkIcon,
  // knowledgeBase: MenuBookIcon,
  conditional: ConditionIcon,
};

const nodeTypes = {
  text: BaseNode,
  image: BaseNode,
  video: BaseNode,
  button: BaseNode,
  list: BaseNode,
  document: BaseNode,
  audio: BaseNode,
  intervention: BaseNode,
  catalog: BaseNode,
  product: BaseNode,
  whatsAppFlow: BaseNode,
  existingFlow: BaseNode,
  address: BaseNode,
  template: BaseNode,
  openAi: BaseNode,
  ctaUrl: BaseNode,
  // knowledgeBase: BaseNode,
  conditional: BaseNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

const ChatCanvas: React.FC = () => {
  const { screenToFlowPosition, fitView } = useReactFlow();
  const actions = FlowBuilderActions();
  const { showSnackBar } = useSnackBar();
  const { flowId } = useParams();
  const navigate = useNavigate();
  // const [nodes, setNodes, onNodesChange] = useNodesState < Node[] > ([]);
  // const [edges, setEdges, onEdgesChange] = useEdgesState < Edge[] > ([]);
  const [nodes, setNodes, onNodesChange] = useNodesState < Node[] > (() => {
    if (!flowId) return [];
    const savedFlow = JSON.parse(localStorage.getItem('flowData') || '{}');
    return savedFlow[flowId]?.nodes || [];
  });

  const [edges, setEdges, onEdgesChange] = useEdgesState < Edge[] > (() => {
    if (!flowId) return [];
    const savedFlow = JSON.parse(localStorage.getItem('flowData') || '{}');
    return savedFlow[flowId]?.edges || [];
  });

  const [flowObj, setFlowObj] = useRecoilState < any > (flowObjAtom);
  const [isLoading, setIsLoading] = useState(false);
  const botFLowData: any = useRecoilValue(botFlowDataAtom);
  const [openTable, setOpenTable] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [selectedSourceNodeId, setSelectedSourceNodeId] = useState < string | null > (null);
  const [showAddNodeModal, setShowAddNodeModal] = useState(false);
  const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);

  // Add this - single selection state for edge
  const [selectedEdgeId, setSelectedEdgeId] = useState < string | null > (null);
  const hasFittedNodes = useRef(false);

  useEffect(() => {
    if (!nodes.length || hasFittedNodes.current) return;

    hasFittedNodes.current = true;
    const firstFrame = requestAnimationFrame(() => {
      requestAnimationFrame(() => fitView({ padding: 0.2 }));
    });

    return () => cancelAnimationFrame(firstFrame);
  }, [fitView, nodes.length]);

useEffect(() => {
  const patterns = botFLowData?.patterns || [];

  const isMatched = patterns.includes(
    'inside flow - keyword found_md_7'
  );

  if (!isMatched) return;

  const alreadyExists = nodes.some(
    (node) =>
      node.type === 'button' ||
      node.type === 'existingFlow'
  );

  if (alreadyExists) return;

  const buttonId = generateId(nodes.length + 1);
  const existingFlowId = generateId(nodes.length + 2);

  const flowData = insideFlowNodes(
    buttonId,
    existingFlowId,
    handleNodeChange,
    onNodesDelete,
    handleOutputClick,
    handleEdgeDelete
  );

  setNodes((prev) => [...prev, ...flowData.nodes]);
  setEdges((prev) => [...prev, ...flowData.edges]);
}, [botFLowData]);

  const handleOutputClick = (nodeId: string) => {
    const node = document.querySelector(`[data-id="${nodeId}"]`);
    if (node) {
      const rect = node.getBoundingClientRect();
      setModalPosition({
        top: rect.top + window.scrollY,
        left: rect.left + rect.width + 10 + window.scrollX, // 10px right offset
      });
    }
    setSelectedSourceNodeId(nodeId);
    setShowAddNodeModal(true);
  };

  const [searchText, setSearchText] = useState('');
  const filteredNodeTypes = useMemo(() => {
    return Object.keys(nodeTypes).filter((type) =>
      type.toLowerCase().includes(searchText.toLowerCase())
    );
  }, [searchText]);

  const handleAddNewNodeFromModal = (type: string) => {
    if (!selectedSourceNodeId) return;

    const sourceNode = nodes.find((n) => n.id === selectedSourceNodeId);
    if (!sourceNode) return;

    const newId = generateId(nodes.length + 1);
    const newPosition = {
      x: sourceNode.position.x + 300,
      y: sourceNode.position.y,
    };

    const newNode: Node = {
      id: newId,
      type,
      position: newPosition,
      data: {
        id: newId,
        type,
        onChange: handleNodeChange,
        onNodesDelete: onNodesDelete,
        onOutputClick: handleOutputClick,
        // onCloneNode: onCloneNode,
      },
    };

    // Check if the source node already has an outgoing edge
    const hasOutgoingEdge = edges.some((edge) => edge.source === sourceNode.id);

    // Add the new node
    setNodes((nds) => [...nds, newNode]);

    // If no outgoing edge exists, connect the new node
    if (!hasOutgoingEdge) {
      const newEdge: Edge = {
        id: `${sourceNode.id}-${newId}-${generateId(edges.length + 1)}`,
        source: sourceNode.id,
        target: newId,
        type: 'custom',
        style: { strokeWidth: 3 },
        markerEnd: { type: 'arrowclosed' },
        data: {
          onDelete: deleteEdge,
          onSelect: handleEdgeSelect,
          isSelected: false,
        },
      };

      setEdges((eds) => [...eds, newEdge]);
    }

    // Reset modal state
    setShowAddNodeModal(false);
    setSelectedSourceNodeId(null);
  };

  function generateId(nodeLength: any) {
    return `${nodeLength}_${Date.now()}`;
  }

  // Memoized onChange handler
  const handleNodeChange = useCallback(
    (id: string, updates: any) => {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                ...updates,
              },
            }
            : node
        )
      );
    },
    [setNodes]
  );

  const onNodesDelete = (nodeId: string | any) => {
    setNodes((prevNodes) => prevNodes.filter((node) => node.id !== nodeId));
    setEdges((prevEdges) =>
      prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId)
    );
    // Clear selection if deleted node's edge was selected
    if (selectedEdgeId) {
      const edgeExists = edges.some((edge) => edge.id === selectedEdgeId);
      if (!edgeExists) {
        setSelectedEdgeId(null);
      }
    }
  };

  // Updated handleEdgeDelete with selection cleanup
  const handleEdgeDelete = (edgeId: string) => {
    setEdges((prevEdges) => prevEdges.filter((edge) => edge.id !== edgeId));
    if (selectedEdgeId === edgeId) {
      setSelectedEdgeId(null);
    }
  };

  // Updated deleteEdge with selection cleanup
  const deleteEdge = (id: string) => {
    setEdges((eds) => eds.filter((edge) => edge.id !== id));
    if (selectedEdgeId === id) {
      setSelectedEdgeId(null);
    }
  };

  // Edge selection handler - only one at a time
  const handleEdgeSelect = useCallback((edgeId: string) => {
    // Toggle - if clicking the same edge, deselect it
    if (selectedEdgeId === edgeId) {
      setSelectedEdgeId(null);
    } else {
      setSelectedEdgeId(edgeId);
    }
  }, [selectedEdgeId]);

  // Click outside handler - deselect edge
  const handlePaneClick = () => {
    setSelectedEdgeId(null);
  };

  // Keyboard shortcuts for edge management
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedEdgeId) {
        deleteEdge(selectedEdgeId);
        showSnackBar('Edge deleted!', 'info');
        setSelectedEdgeId(null);
      }
      if (e.key === 'Escape' && selectedEdgeId) {
        setSelectedEdgeId(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEdgeId]);

  // Update edges when selection changes
  useEffect(() => {
    setEdges((currentEdges) => 
      currentEdges.map((edge) => ({
        ...edge,
        data: {
          ...edge.data,
          isSelected: edge.id === selectedEdgeId,
          onSelect: handleEdgeSelect,
          onDelete: deleteEdge,
        },
      }))
    );
  }, [selectedEdgeId, handleEdgeSelect]);

  useEffect(() => {
    if (!flowId) return;
 
    const store = JSON.parse(localStorage.getItem('flowData') || '{}');
    store[flowId] = { nodes, edges };
 
    localStorage.setItem('flowData', JSON.stringify(store));
  }, [nodes, edges]);

  useEffect(() => {
    const data = { nodes, edges };
    if (JSON.stringify(flowObj) !== JSON.stringify(data)) {
      // processNodesToSetEntityData(nodes);
      // Step 1: Create adjacency list for incoming edges
      const incomingEdgesMap = new Map < string, string[]> ();
      nodes.forEach((node) => incomingEdgesMap.set(node.id, []));

      edges.forEach((edge) => {
        incomingEdgesMap.get(edge.target)?.push(edge.source);
      });

      // Step 2: Identify root nodes (nodes with no incoming edges)
      const rootNodes = nodes.filter((node) => incomingEdgesMap.get(node.id)?.length === 0);

      // Step 3: Sort nodes based on depth (Kahn's Algorithm for Topological Sorting)
      const sortedNodes: Node[] = [];
      const visited = new Set();
      const inDegree = new Map < string, number> ();

      // Initialize in-degrees
      nodes.forEach((node) => {
        inDegree.set(node.id, incomingEdgesMap.get(node.id)?.length || 0);
      });

      const queue = [...rootNodes];

      while (queue.length > 0) {
        const node = queue.shift()!;
        sortedNodes.push(node);
        visited.add(node.id);

        // Reduce the in-degree of connected nodes
        edges.forEach((edge) => {
          if (edge.source === node.id) {
            const targetNode = nodes.find((n) => n.id === edge.target)!;
            inDegree.set(edge.target, (inDegree.get(edge.target) || 0) - 1);

            if (inDegree.get(edge.target) === 0) {
              queue.push(targetNode);
            }
          }
        });
      }

      // If all nodes were not visited, we have a cycle
      if (sortedNodes.length !== nodes.length) {
        console.warn('Cycle detected! Keeping original node order.');
        setNodes(nodes);
        setFlowObj(data);
        // storeDataInLocalStorage();
        return;
      }

      // Step 4: Assign headers based on sorted order
      const updatedNodes = sortedNodes.map((node, index) => {
        let newData = {
          ...node.data,
          nodeCount: `${index + 1}`,
          parentResults: [], //getAllParentResultsRecursive(node.id, nodes, edges),
        };
        return {
          ...node,
          data: newData,
        };
      });
      setNodes(updatedNodes);
      setFlowObj(data);
      // storeDataInLocalStorage();

    }
  }, [nodes, edges, flowObj, setFlowObj]);

  // Updated onConnect with edge selection support
 const onConnect = useCallback(
  (connection: Connection | any) => {
    if (connection.source === connection.target) {
      showSnackBar('A node cannot connect to itself.', 'error');
      return;
    }
    const isSourceHandleUsed = edges.some(
      (edge) => edge.source === connection.source && edge.sourceHandle === connection.sourceHandle
    );

    if (!isSourceHandleUsed) {
      const newEdge = {
        ...connection,
        id: `${connection.source}-${connection.target}-${generateId(edges?.length + 1)}`,
        type: 'custom',
        style: { strokeWidth: 3 },
        markerEnd: { type: 'arrowclosed' },
        data: {
          onDelete: deleteEdge,
          onSelect: handleEdgeSelect,
          isSelected: false,
        },
      };
      setEdges((eds) => addEdge(newEdge, eds));
    } else {
      showSnackBar(
        'This endpoint already has an outgoing edge. You cannot connect multiple edges from one endpoint.',
        'error'
      );
    }
  },
  [edges, setEdges, handleEdgeSelect] 
);

  const onDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();

    const reactFlowBounds = (event.target as HTMLElement).getBoundingClientRect();
    const type = event.dataTransfer.getData('application/reactflow');

    if (!type) return;

    const position = screenToFlowPosition({
      x: event.clientX,
      y: event.clientY,
    });

    const id = generateId(nodes.length + 1);

    const newNode: Node = {
      id,
      type,
      position,
      data: {
        id,
        type,
        header: '',
        body: '',
        footer: '',
        text: '',
        sourceType: '',
        options: type === 'list' ? [{ title: '', options: [''] }] : undefined,
        buttons: type === 'button' ? [''] : undefined,
        ...(type === 'intervention' && {
          intervention: [
            {
              type: 'click_to_call',
              value: 'Click to call',
              label: 'Click to call',
              enabled: false,
            },
            {
              type: 'request_to_callback',
              value: 'Request to call back',
              label: 'Request to call back',
              enabled: false,
            },
            {
              type: 'chat_with_agent',
              value: 'Chat with Agent',
              label: 'Chat with Agent',
              enabled: false,
            },
          ],
        }),
        media: {},
        onChange: handleNodeChange,
        onNodesDelete: onNodesDelete,
        onOutputClick: handleOutputClick,
        // onCloneNode: onCloneNode,
      },
    };

    setNodes((nds) => nds.concat(newNode));
  };

  // Function to delete an edge
  // const deleteEdge = (id: string) => {
  //   setEdges((eds) => eds.filter((edge) => edge.id !== id));
  // };

  const fetchNodeEdgeById = async () => {
    setIsLoading(true);
    try {
      let res = await actions.getNodeEdgesById(flowId);
      if (res) {
        const nodes = res?.nodes || [];
        const edges = res?.edges || [];

        const updatedNodes = nodes.map((node) => ({
          ...node,
          data: {
            ...node.data,
            onChange: handleNodeChange,
            onNodesDelete: onNodesDelete,
            onOutputClick: handleOutputClick,
            // onCloneNode: onCloneNode,
          },
        }));

        const updatedEdges = edges.map((edge) => ({
          ...edge,
          type: 'custom',
          data: {
            ...edge.data,
            onDelete: handleEdgeDelete,
            onSelect: handleEdgeSelect,
            isSelected: edge.id === selectedEdgeId,
          },
        }));

        setNodes(updatedNodes);
        setEdges(updatedEdges);
      }
    } catch (error) {
      console.warn(error);
    } finally {
      setIsLoading(false);
    }
  };

  const proOptions = { hideAttribution: true };

  // Updated useEffect for loading saved data with edge selection support
  useEffect(() => {
    if (!flowId) return;

    hasFittedNodes.current = false;
    const savedFlows = JSON.parse(localStorage.getItem('flowData') || '{}');
    const saved = savedFlows[flowId];

    const hasValidData =
      saved &&
      ((Array.isArray(saved.nodes) && saved.nodes.length > 0) ||
        (Array.isArray(saved.edges) && saved.edges.length > 0));

    if (hasValidData) {
      const updatedNodes = (saved.nodes || []).map((node) => ({
        ...node,
        data: {
          ...node.data,
          onChange: handleNodeChange,
          onNodesDelete: onNodesDelete,
          onOutputClick: handleOutputClick,
          // onCloneNode: onCloneNode,
        },
      }));

      const updatedEdges = (saved.edges || []).map((edge) => ({
        ...edge,
        type: edge.type || 'custom',
        data: {
          ...(edge.data || {}),
          onDelete: handleEdgeDelete,
          onSelect: handleEdgeSelect,
          isSelected: edge.id === selectedEdgeId,
        },
      }));

      setNodes(updatedNodes);
      setEdges(updatedEdges);
    } else {
      fetchNodeEdgeById();
    }
  }, [flowId]);


  const handleTableChange = (updatedNodes: Node[]) => {
    setNodes(updatedNodes);
  };

  return (
    <>
      <Box
        sx={{
          height: '100%',
          minHeight: 0,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
      <Box
        sx={{
          padding: '10px 15px',
          backgroundColor: '#f5f5f5',
          borderRadius: '8px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="body1">
          <strong>Bot Flow Name:</strong> {botFLowData?.title || ''}
        </Typography>
        {botFLowData?.organisation_name && (
          <Typography variant="body1">
            <strong>Organisation:</strong> {botFLowData.organisation_name}
          </Typography>
        )}
        {botFLowData?.campaign_name && (
          <Typography variant="body1">
            <strong>Campaign:</strong> {botFLowData.campaign_name}
          </Typography>
        )}
        {botFLowData?.leads_form_name && (
          <Typography variant="body1">
            <strong>Webform:</strong> {botFLowData?.leads_form_name}
          </Typography>
        )}
        <Typography variant="body1">
          <strong>WhatsApp Number:</strong> {botFLowData?.phone_number_id}
        </Typography>
        <Box
          sx={{
            position: 'fixed',
            top: 80,
            right: 20,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            zIndex: 1300,
          }}
        >
          {fabOpen && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 1,
                backgroundColor: '#fff',
                boxShadow: 3,
                borderRadius: 2,
                padding: '10px',
                minWidth: '160px',
              }}
            >
              <Zoom in>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => setOpenTable(true)}
                >
                  Show Table
                </Button>
              </Zoom>
              <Zoom in>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => navigate('/r/flow/schedule-messages/' + flowId)}
                >
                  Schedule Message
                </Button>
              </Zoom>
              <Zoom in>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={() => handleDownloadExcelFile(nodes, edges, showSnackBar)}
                >
                  Download Excel
                </Button>
              </Zoom>
            </Box>
          )}

          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={() => setFabOpen((prev) => !prev)}
            startIcon={fabOpen ? <CloseIcon /> : <MenuIcon />}
            sx={{ borderRadius: '20px' }}
          >
            {fabOpen ? 'Close' : 'Actions'}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <LoadingWrapper />
        </Box>
      ) : (
        <ReactFlow
          style={{ flex: 1, minHeight: 0, width: '100%' }}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          onNodesDelete={onNodesDelete}
          edgeTypes={edgeTypes}
          onDragOver={onDragOver}
          onDrop={onDrop}
          proOptions={proOptions}
          fitView
          onPaneClick={handlePaneClick}
        >
          {nodes.length > 0 && (
            <MiniMap
              nodeColor={(node) => node.style?.backgroundColor || '#eee'}
              nodeStrokeColor={(node) => node.style?.borderColor || '#001F3F'}
              nodeBorderRadius={5}
              zoomable
              pannable
              style={{ position: 'absolute', bottom: 52, left: 30, width: 150, height: 100 }}
            />
          )}
          <Controls style={{ bottom: 52 }} />
          <Background />
        </ReactFlow>
      )}
      <ShowTableModal
        open={openTable}
        onClose={() => setOpenTable(false)}
        nodes={nodes}
        edges={edges}
        onUpdateNodes={handleTableChange}
        onUpdateEdges={(updatedEdges) => setEdges(updatedEdges)} 
      />
      {showAddNodeModal && (
        <Box
          sx={{
            position: 'absolute',
            top: modalPosition.top + dragOffset.y,
            left: modalPosition.left + dragOffset.x,
            zIndex: 2000,
            cursor: isDragging ? 'grabbing' : 'default',
          }}
          onMouseMove={(e) => {
            if (isDragging) {
              setDragOffset((prev) => ({
                x: e.clientX - prev.startX,
                y: e.clientY - prev.startY,
                startX: prev.startX,
                startY: prev.startY,
              }));
            }
          }}
          onMouseUp={() => setIsDragging(false)}
          onMouseLeave={() => setIsDragging(false)}
        >
          <Paper
            elevation={4}
            sx={{
              backgroundColor: '#fff',
              padding: 3,
              borderRadius: 2,
              minWidth: 250,
              maxHeight: '60vh',
              position: 'relative',
            }}
          >
            {/* Drag Handle (Header) */}
            <Box
              sx={{
                cursor: 'grab',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
              onMouseDown={(e) => {
                setIsDragging(true);
                setDragOffset({
                  x: dragOffset.x,
                  y: dragOffset.y,
                  startX: e.clientX,
                  startY: e.clientY,
                });
              }}
            >
              <Typography variant="h6">Select Node Type</Typography>
              <IconButton size="small" onClick={() => setShowAddNodeModal(false)}>
                <CloseIcon />
              </IconButton>
            </Box>

            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search node types"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                maxHeight: '30vh',
                overflowY: 'auto',
              }}
            >
              {filteredNodeTypes.map((type) => {
                const IconComponent = nodeIcons[type];
                return (
                  <Button
                    key={type}
                    variant="outlined"
                    size="small"
                    onClick={() => handleAddNewNodeFromModal(type)}
                    fullWidth
                    startIcon={IconComponent ? <IconComponent fontSize="small" /> : null}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)} Node
                  </Button>
                );
              })}
            </Box>
          </Paper>
        </Box>
      )}
      </Box>
    </>
  );
};

export default () => (
  <ReactFlowProvider>
    <ChatCanvas />
  </ReactFlowProvider>
);